import { useRef, useState } from 'react';
import Modal from '@sdk/components/ui/modal/Modal';
import gettext, { pgettext } from '@sdk/lang/gettext';
import Text12 from '@sdk/components/ui/typography/Text12';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Spinner from '@sdk/components/ui/Spinner';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { LocationType } from '@sdk/locations/types/LocationType';

import LocationChecklist from '@web/shifts/LocationChecklist/LocationChecklist';
import BodyPortal from '@web/components/BodyPortal';
import { DeleteConfirmModal } from '@web/components/DeleteConfirmModal/DeleteConfirmModal';
import IcalBlueSvg from '@web/assets/svg/Icons/32/ical-blue.svg';
import {
  ReadonlyTextField,
  ActionConfirmation,
  LinkFieldContainer,
  ActionButtonContainer,
} from '@web/shifts/EnableIcalModal/enableIcalModal.styles';
import { useEnableIcalModal } from '@web/shifts/EnableIcalModal/enableIcalModal.hooks';
import ActionButton from '@web/shifts/EnableIcalModal/ActionButton';
import IcalOptionsMenu from '@web/shifts/EnableIcalModal/IcalOptionsMenu';

type Props = {
  opened: boolean;
  onClose: () => void;
  currentUser: AuthUserType;
  locations: LocationType[];
};

const EnableIcalModal = ({ opened, onClose, currentUser, locations }: Props) => {
  const config = useRef({ noDismiss: true }).current;
  const sortedLocations = locations.sort((a, b) => a.name.localeCompare(b.name));
  const {
    syncComplete,
    personalICalLink,
    entireICalLink,
    resetICal,
    deleteICal,
    isPersonalCopied,
    setIsPersonalCopied,
    isEntireCopied,
    setIsEntireCopied,
    isVisible,
    disableConfirmationModalState,
    userLocations,
    handleLocationClick,
    clearCheckedIds,
    onICalSubmit,
    hasChanges,
  } = useEnableIcalModal(opened, currentUser, sortedLocations);
  const [isPersonal, setIsPersonal] = useState(false);

  const openDeleteModal = (isPersonal) => {
    setIsPersonal(isPersonal);
    disableConfirmationModalState.open();
  };

  const iCalLinkContent = syncComplete ? (
    <>
      <Row>
        <Text14 themeColor={ThemeColorType.gray}>
          {gettext("Paste the link for the schedule you'd like to sync into your calendar app:")}
        </Text14>
      </Row>
      <Row mTop={10}>
        <Text12 themeColor={ThemeColorType.gray} semiBold>
          {gettext('Your schedule:')}
        </Text12>
      </Row>
      <Row mTop={5}>
        <LinkFieldContainer>
          <ReadonlyTextField
            placeholder={gettext('Generate url of calendar')}
            value={personalICalLink}
            readOnly
            skipErrorMessage
            paddingRight={35}
          />
          <ActionConfirmation isShown={isPersonalCopied}>{gettext('Copied!')}</ActionConfirmation>
        </LinkFieldContainer>
        <ActionButtonContainer>
          <ActionButton
            onCopy={() => setIsPersonalCopied(true)}
            onSubmit={onICalSubmit}
            isPersonal={true}
            text={personalICalLink}
            hasChanges={!personalICalLink}
          />
        </ActionButtonContainer>
        <IcalOptionsMenu
          openDeleteModal={openDeleteModal}
          onReset={resetICal}
          isPersonal={true}
          iCalCreated={!!personalICalLink}
        />
      </Row>
      {!!userLocations.length && (
        <>
          <Row mTop={10}>
            <Text12 themeColor={ThemeColorType.gray} semiBold>
              {gettext('Entire schedule:')}
            </Text12>
          </Row>
          <Row mTop={5}>
            {userLocations.length > 1 && (
              <LocationChecklist
                userLocations={userLocations}
                handleLocations={handleLocationClick}
                clearIds={clearCheckedIds}
              />
            )}
            <LinkFieldContainer>
              <ReadonlyTextField
                placeholder={gettext('Generate url of calendar')}
                value={entireICalLink}
                readOnly
                skipErrorMessage
                paddingRight={35}
              />
              <ActionConfirmation isShown={isEntireCopied}>{gettext('Copied!')}</ActionConfirmation>
            </LinkFieldContainer>
            <ActionButtonContainer>
              <ActionButton
                onCopy={() => setIsEntireCopied(true)}
                onSubmit={onICalSubmit}
                isPersonal={false}
                text={entireICalLink}
                hasChanges={hasChanges}
              />
            </ActionButtonContainer>

            <IcalOptionsMenu
              openDeleteModal={openDeleteModal}
              onReset={resetICal}
              isPersonal={false}
              iCalCreated={!!entireICalLink}
            />
          </Row>
        </>
      )}
    </>
  ) : (
    <Spinner />
  );

  return (
    <Modal fixed config={config} isVisible={isVisible} onDismiss={onClose} closePositionTop={10}>
      <ModalBodyWithHeader
        width={620}
        titleThemeColor={ThemeColorType.primary}
        titleText={gettext('Sync to calendar apps')}
        titleIcon={<IcalBlueSvg />}
        close={onClose}>
        {iCalLinkContent}
      </ModalBodyWithHeader>
      <BodyPortal>
        <DeleteConfirmModal
          modalState={disableConfirmationModalState}
          onSubmit={() => deleteICal(isPersonal)}
          title={gettext('Disable sync to your calendar app')}
          description={gettext(
            'Are you sure you want to stop synching your schedule from Sling to your calendar app?\nWe will remove any previously synched shifts from your personal calendar.'
          )}
          primaryButtonText={pgettext('Stop iCal sync', 'Disable')}
        />
      </BodyPortal>
    </Modal>
  );
};

export default EnableIcalModal;
